/**
 * What the shell knows about the signed-in user, whichever provider the build
 * carries.
 *
 * The provider-specific code (`src/auth/cognito/`) is lazy-loaded only in a
 * cognito build, so nothing outside that folder may import it — the account
 * screen, the shell and the route guard read THIS context instead. In a `none`
 * build the default value applies: always signed in, nobody to show, nothing to
 * sign out of, and the guard is a pass-through.
 */

import { createContext, useContext } from 'react';
import type { AuthProvider } from '@/config/auth';

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut';

export interface AuthUser {
  readonly email: string | null;
  readonly displayName: string | null;
}

export interface AuthShellValue {
  readonly provider: AuthProvider;
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** Re-read the session after a login completed on the login screen. */
  readonly refresh: () => Promise<void>;
  /** `null` when the build has no login to sign out of. */
  readonly signOut: (() => Promise<void>) | null;
}

export const NO_AUTH_SHELL: AuthShellValue = {
  provider: 'none',
  status: 'signedIn',
  user: null,
  refresh: () => Promise.resolve(),
  signOut: null,
};

export const AuthShellContext = createContext<AuthShellValue>(NO_AUTH_SHELL);

export const useAuthShell = (): AuthShellValue => useContext(AuthShellContext);
